import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CustomerInsights from '../../components/CustomerInsights';
import { API_ENDPOINTS } from '../../config/api';

const DASHBOARD_TABS_CONFIG = [
    { id: 'menu', label: 'Menu & Tables', icon: '🍽️' },
    { id: 'orderflow', label: 'Order Flow', icon: '🔄' },
    { id: 'counter', label: 'Counter Orders', icon: '🛒' },
    { id: 'inventory', label: 'Inventory & Recipes', icon: '📦' },
    { id: 'staff', label: 'Staff Management', icon: '👥' },
    { id: 'offers', label: 'Special Offers', icon: '🎁' },
    { id: 'analytics', label: 'Food Analytics', icon: '📊' },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'profile', label: 'Restaurant Profile', icon: '🏪' }
];

const defaultDashboardTabs = Object.fromEntries(DASHBOARD_TABS_CONFIG.map(t => [t.id, true]));

const RestaurantDetails = ({ restaurant, onUpdateRestaurant }) => {
    const [editableRestaurant, setEditableRestaurant] = useState({ ...restaurant });
    const [error, setError] = useState(null);

    // Ensure that editableRestaurant updates when the restaurant prop changes
    useEffect(() => {
        const mergedTabs = { ...defaultDashboardTabs, ...(restaurant.dashboardTabs || {}) };
        setEditableRestaurant({
            ...restaurant,
            taxPercentage: restaurant.taxPercentage !== undefined && restaurant.taxPercentage !== null 
                ? String(restaurant.taxPercentage) 
                : '5',
            dashboardTabs: mergedTabs,
            password: ''
        });
    }, [restaurant]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        
        // For taxPercentage, store as string to preserve exact decimal values
        if (name === 'taxPercentage') {
            // Validate that it's a valid number format
            const isValidNumber = /^-?\d*\.?\d*$/.test(value);
            if (isValidNumber || value === '') {
                processedValue = value; // Store as string
            } else {
                return; // Invalid input, don't update
            }
        }
        
        setEditableRestaurant({ ...editableRestaurant, [name]: processedValue });
    };

    const handleDashboardTabToggle = (tabId) => {
        const current = editableRestaurant.dashboardTabs || { ...defaultDashboardTabs };
        setEditableRestaurant({
            ...editableRestaurant,
            dashboardTabs: { ...current, [tabId]: !current[tabId] }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Convert taxPercentage from string to number before saving
            const dataToSave = {
                ...editableRestaurant,
                taxPercentage: editableRestaurant.taxPercentage !== '' && editableRestaurant.taxPercentage != null
                    ? Number(String(editableRestaurant.taxPercentage).trim()) || 0
                    : 0
            };
            
            // Only include password if it's been changed (not empty)
            // If password field is empty, don't send it (backend will keep existing password)
            if (!dataToSave.password || dataToSave.password.trim() === '') {
                delete dataToSave.password;
            }
            
            await onUpdateRestaurant(restaurant._id, dataToSave); // Update restaurant
        } catch (err) {
            setError('Failed to update restaurant');
        }
    };

    const handlePaymentHistory = async (id) => {
        try {
            await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(id));
        } catch (err) {
            setError('restaurant ID is missing');
        }
    }

    return (
        <div className="space-y-6">
            {/* Edit Restaurant Form */}
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-3xl shadow-2xl border border-white/20">
                <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl">✏️</span>
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                        Edit {editableRestaurant.name}
                    </h2>
                </div>
                
                {error && (
                    <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-4 rounded-2xl mb-6 flex items-center gap-3">
                        <span className="text-xl">⚠️</span>
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Restaurant Info */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>🏢</span>
                            Restaurant Information
                        </h3>
                        <div className="space-y-4">
                            <input
                                type="text"
                                name="name"
                                value={editableRestaurant.name}
                                onChange={handleChange}
                                placeholder="Restaurant Name"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="text"
                                name="owner"
                                value={editableRestaurant.owner}
                                onChange={handleChange}
                                placeholder="Owner Name"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="text"
                                name="logo"
                                value={editableRestaurant.logo}
                                onChange={handleChange}
                                placeholder="Logo URL"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>📞</span>
                            Contact Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="phone"
                                value={editableRestaurant.phone}
                                onChange={handleChange}
                                placeholder="Phone Number"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="email"
                                name="email"
                                value={editableRestaurant.email}
                                onChange={handleChange}
                                placeholder="Email"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Business Info */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>💼</span>
                            Business Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                                type="text"
                                name="GST"
                                value={editableRestaurant.GST}
                                onChange={handleChange}
                                placeholder="GST Number"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                            <input
                                type="text"
                                inputMode="decimal"
                                name="taxPercentage"
                                value={editableRestaurant.taxPercentage !== undefined && editableRestaurant.taxPercentage !== null ? String(editableRestaurant.taxPercentage) : '5'}
                                onChange={handleChange}
                                onBlur={(e) => {
                                    // Format the value on blur - ensure it's a valid number
                                    const value = e.target.value.trim();
                                    if (value !== '') {
                                        const numValue = Number(value);
                                        if (!isNaN(numValue)) {
                                            // Clamp value between 0 and 100
                                            const clampedValue = Math.max(0, Math.min(100, numValue));
                                            // Preserve decimal places if user entered them
                                            const decimalPlaces = value.includes('.') ? value.split('.')[1]?.length || 0 : 0;
                                            const formattedValue = decimalPlaces > 0 
                                                ? clampedValue.toFixed(Math.min(decimalPlaces, 2))
                                                : String(clampedValue);
                                            setEditableRestaurant(prev => ({
                                                ...prev,
                                                taxPercentage: formattedValue
                                            }));
                                        }
                                    }
                                }}
                                placeholder="Tax Percentage"
                                pattern="[0-9]*\.?[0-9]*"
                                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                required
                            />
                        </div>
                    </div>

                    {/* Dashboard Tabs (Subscription / visibility) */}
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <span>📑</span>
                            Dashboard Tabs
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Show or hide sections in this restaurant&apos;s dashboard (e.g. by subscription package). Only visible when ticked.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {DASHBOARD_TABS_CONFIG.map((tab) => {
                                const tabs = editableRestaurant.dashboardTabs || defaultDashboardTabs;
                                const isEnabled = tabs[tab.id] !== false;
                                return (
                                    <label
                                        key={tab.id}
                                        className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                                            isEnabled ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={() => handleDashboardTabToggle(tab.id)}
                                            className="w-5 h-5 rounded text-green-600 focus:ring-green-500"
                                        />
                                        <span className="text-lg">{tab.icon}</span>
                                        <span className="font-medium text-gray-800">{tab.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* Security */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-2xl">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <span>🔒</span>
                            Security
                        </h3>
                        <input
                            type="password"
                            name="password"
                            value={editableRestaurant.password}
                            onChange={handleChange}
                            placeholder="Password (leave blank to keep current)"
                            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <span>💾</span>
                            Update Restaurant
                        </button>
                        <button
                            type="button"
                            onClick={() => handlePaymentHistory(restaurant._id)}
                            className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            <span>💳</span>
                            Payment History
                        </button>
                    </div>
                </form>
            </div>

            {/* Customer Insights Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <span>📊</span>
                        Customer Analytics
                    </h3>
                </div>
                <div className="p-6">
                    <CustomerInsights restaurantId={restaurant._id} />
                </div>
            </div>
        </div>
    );
};

export default RestaurantDetails;
